import React, { useLayoutEffect, useRef, useState } from 'react';
import { Collapse, Modal } from 'antd';
import { ArrowLeftOutlined, CaretDownOutlined, MinusOutlined, PlusOutlined } from '@ant-design/icons';
import parse, { domToReact } from 'html-react-parser';
import content from './content.json';
import terms from './terms.json';
import './style.scss';

function RichText({ html, onOpen }) {
  return parse(html, {
    trim: true,
    replace: (node) => {
      const target = node.attribs?.['data-detail'];
      if (node.name === 'button' && target && terms.entries[target]) {
        return <button type="button" className="wyndham-detail-link" onClick={() => onOpen(target)}>{domToReact(node.children)}</button>;
      }
      return undefined;
    },
  });
}

export default function WyndhamBenefits() {
  const pageRef = useRef(null);
  const dialogBodyRef = useRef(null);
  const [detailHistory, setDetailHistory] = useState([]);
  const detailId = detailHistory[detailHistory.length - 1];
  const detail = terms.entries[detailId];

  useLayoutEffect(() => {
    const container = pageRef.current.closest('.container');
    if (!container) return;
    container.classList.add('wyndham-benefits-container');
    return () => container.classList.remove('wyndham-benefits-container');
  }, []);

  useLayoutEffect(() => {
    if (detailId && dialogBodyRef.current) {
      dialogBodyRef.current.scrollTop = 0;
      dialogBodyRef.current.focus();
    }
  }, [detailId]);

  const openDetail = (id) => {
    if (!terms.entries[id]) return;
    setDetailHistory((history) => history[history.length - 1] === id ? history : [...history, id]);
  };

  return (
    <main ref={pageRef} className="wyndham-benefits" lang="zh-CN">

      <h1 className="hotel-member-title fs-21">
        <img className="hotel-member-logo asr-logo" src="https://staticfile.badazhou.com/20260922/37e58e4e2ff89d37a305bd4b278d19a2.png" alt="" />
        <span>温德姆奖赏计划-会员礼遇</span>
      </h1>

      <header className="wyndham-benefits__intro">
        <h1>{content.heading}</h1>
        <div className="wyndham-benefits__intro-copy"><RichText html={content.intro} onOpen={openDetail} /></div>
      </header>
      

      <section className="wyndham-benefits__levels" aria-label="会员等级福利">
        <Collapse
          className="wyndham-benefits__tiers"
          ghost
          expandIconPosition="end"
          expandIcon={({ isActive }) => <CaretDownOutlined rotate={isActive ? 180 : 0} aria-hidden="true" />}
          items={content.tiers.map((tier) => ({
            key: tier.id,
            label: (
              <div className="wyndham-benefits__tier-label">
                <span className="wyndham-benefits__qualification">{tier.qualification}</span>
                <span className={`wyndham-benefits__tier-name wyndham-benefits__${tier.id}`}>{tier.name}</span>
              </div>
            ),
            children: (
              <section aria-label={`${tier.name}权益`}>
                <Collapse
                  className="wyndham-benefits__perks"
                  ghost
                  expandIconPosition="end"
                  expandIcon={({ isActive }) => isActive ? <MinusOutlined aria-hidden="true" /> : <PlusOutlined aria-hidden="true" />}
                  items={tier.perks.map((item) => {
                    const perk = content.perks[item.id];
                    return {
                      key: item.id,
                      label: perk.paragraphTitle ? <p>{perk.title}</p> : perk.title,
                      children: (
                        <div className="wyndham-benefits__perk-detail" role="region" aria-label={`${tier.name}：${perk.title}`}>
                          <div className="wyndham-benefits__description"><RichText html={perk.html} onOpen={openDetail} /></div>
                          {item.offer && <div className="wyndham-benefits__offer">{item.offer}</div>}
                        </div>
                      ),
                    };
                  })}
                />
              </section>
            ),
          }))}
        />
      </section>

      <Modal
        open={Boolean(detail)}
        onCancel={() => setDetailHistory([])}
        title={<span className="wyndham-benefits__sr-only">{detail?.title}</span>}
        footer={null}
        centered
        destroyOnClose
        maskStyle={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
        width={720}
        className="wyndham-benefits-dialog"
      >
        {detail && (
          <>
            {detailHistory.length > 1 && (
              <button
                type="button"
                className="wyndham-benefits-dialog__back"
                aria-label="返回上一条说明"
                title="返回上一条说明"
                onClick={() => setDetailHistory((history) => history.slice(0, -1))}
              ><ArrowLeftOutlined aria-hidden="true" /></button>
            )}
            <div className="wyndham-benefits-dialog__body" key={detailId} ref={dialogBodyRef} tabIndex={-1}>
              {detail.showHeading && <h2>{detail.title}</h2>}
              <RichText html={detail.html} onOpen={openDetail} />
              {detail.related?.map((id) => (
                <p key={id}><button type="button" className="wyndham-detail-link" onClick={() => openDetail(id)}>{terms.entries[id].title}</button></p>
              ))}
            </div>
          </>
        )}
      </Modal>
    </main>
  );
}
